import { useEffect, useState, useMemo } from "react";
import { useWallet } from "@/context/WalletContext";
import { getPortfolio, fmtUsd, fmtNum, shortAddr } from "@/lib/api";
import { PageHeader, StatCard, StatusBadge, ProductTag } from "@/dashboard/ui";

const FILTERS = [
  { id: "all", label: "All" },
  { id: "active", label: "Active" },
  { id: "claimable", label: "Claimable" },
  { id: "unlocked", label: "Unlocked" },
];

export default function Portfolio() {
  const { address } = useWallet();
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    getPortfolio(address).then(setData).catch(() => {});
  }, [address]);

  const positions = useMemo(() => {
    if (!data) return [];
    return filter === "all" ? data.positions : data.positions.filter((p) => p.status === filter);
  }, [data, filter]);

  return (
    <div>
      <PageHeader eyebrow="your positions" title="Portfolio" desc="Every lock, vesting schedule and LP position owned by your connected wallet." />

      <div className="mb-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard testId="pf-total" label="Total Value" value={data ? fmtUsd(data.total_value) : "--"} accent />
        <StatCard testId="pf-active" label="Active" value={data ? fmtUsd(data.active_value) : "--"} />
        <StatCard testId="pf-claimable" label="Claimable" value={data ? fmtUsd(data.claimable_value) : "--"} />
        <StatCard testId="pf-count" label="Positions" value={data ? fmtNum(data.count) : "--"} />
      </div>

      <div className="mb-4 flex gap-2">
        {FILTERS.map((f) => (
          <button
            key={f.id}
            data-testid={`filter-${f.id}`}
            onClick={() => setFilter(f.id)}
            className={`border px-4 py-2 font-mono text-xs transition-colors ${
              filter === f.id ? "border-welock-accent bg-welock-accent/10 text-welock-accent" : "border-welock-border text-welock-textSecondary hover:border-welock-hairline"
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      <div className="border border-welock-border bg-welock-surface" data-testid="portfolio-table">
        <div className="hidden grid-cols-12 gap-2 border-b border-welock-border px-5 py-3 font-mono text-[10px] uppercase tracking-wider text-welock-textMuted md:grid">
          <div className="col-span-3">Asset</div>
          <div className="col-span-2">Type</div>
          <div className="col-span-2">Amount</div>
          <div className="col-span-2 text-right">Value</div>
          <div className="col-span-2">Unlock</div>
          <div className="col-span-1 text-right">Status</div>
        </div>
        <div className="max-h-[560px] overflow-y-auto">
          {positions.map((p) => (
            <div key={p.id} data-testid={`pf-row-${p.id}`} className="grid grid-cols-12 items-center gap-2 border-b border-welock-border/60 px-5 py-3.5 last:border-0 hover:bg-welock-surfaceHover">
              <div className="col-span-6 md:col-span-3">
                <div className="text-sm text-welock-textPrimary">{p.token_symbol}</div>
                <div className="font-mono text-[10px] text-welock-textMuted">{shortAddr(p.token_address)}</div>
              </div>
              <div className="col-span-2 hidden md:block"><ProductTag product={p.product} /></div>
              <div className="col-span-2 hidden font-mono text-xs text-welock-textSecondary md:block">{fmtNum(p.amount)}</div>
              <div className="col-span-3 text-right font-mono text-sm text-welock-textPrimary md:col-span-2">{fmtUsd(p.value_usd)}</div>
              <div className="col-span-2 hidden font-mono text-xs text-welock-textSecondary md:block">{new Date(p.unlock_date).toLocaleDateString()}</div>
              <div className="col-span-3 flex justify-end md:col-span-1"><StatusBadge status={p.status} /></div>
            </div>
          ))}
          {positions.length === 0 && (
            <div className="px-5 py-12 text-center font-mono text-xs text-welock-textMuted">No positions in this filter</div>
          )}
        </div>
      </div>
    </div>
  );
}
